import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Calendar, Coffee, UtensilsCrossed, Moon, Sun, Check } from 'lucide-react';
export type Schedule = {
  alwaysAvailable: boolean;
  days: number[]; // 0=Mon, 1=Tue, ..., 6=Sun
  startTime: string; // "HH:MM" 24h
  endTime: string; // "HH:MM" 24h
};
export const DEFAULT_SCHEDULE: Schedule = {
  alwaysAvailable: true,
  days: [0, 1, 2, 3, 4, 5, 6],
  startTime: '09:00',
  endTime: '21:00'
};
const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DAY_FULL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRESETS: {
  label: string;
  icon: React.ReactNode;
  schedule: Schedule;
}[] = [{
  label: 'Breakfast',
  icon: <Coffee size={14} data-id="element-3261" />,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4, 5, 6],
    startTime: '06:00',
    endTime: '11:00'
  }
}, {
  label: 'Lunch',
  icon: <UtensilsCrossed size={14} data-id="element-3262" />,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4, 5, 6],
    startTime: '11:00',
    endTime: '15:00'
  }
}, {
  label: 'Dinner',
  icon: <Sun size={14} data-id="element-3263" />,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4, 5, 6],
    startTime: '17:00',
    endTime: '22:00'
  }
}, {
  label: 'Late night',
  icon: <Moon size={14} data-id="element-3264" />,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4, 5, 6],
    startTime: '22:00',
    endTime: '02:00'
  }
}, {
  label: 'Weekdays',
  icon: <Calendar size={14} data-id="element-3265" />,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4],
    startTime: '09:00',
    endTime: '17:00'
  }
}, {
  label: 'Weekends',
  icon: <Calendar size={14} data-id="element-3266" />,
  schedule: {
    alwaysAvailable: false,
    days: [5, 6],
    startTime: '09:00',
    endTime: '21:00'
  }
}];
export function formatTime24(time24: string): string {
  // Input is already "HH:MM" 24h format from <input type="time"> — return as-is
  return time24;
}
export function formatDaysShort(days: number[]): string {
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && [0, 1, 2, 3, 4].every(d => days.includes(d))) return 'Weekdays';
  if (days.length === 2 && days.includes(5) && days.includes(6)) return 'Weekends';
  if (days.length === 0) return 'Never';
  // Check for consecutive range
  const sorted = [...days].sort((a, b) => a - b);
  const isConsecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (isConsecutive && sorted.length > 2) {
    return `${DAY_FULL[sorted[0]]}–${DAY_FULL[sorted[sorted.length - 1]]}`;
  }
  return sorted.map(d => DAY_FULL[d]).join(', ');
}
export function formatSchedule(schedule: Schedule): string {
  if (schedule.alwaysAvailable) return 'Always available';
  const days = formatDaysShort(schedule.days);
  const time = `${formatTime24(schedule.startTime)}–${formatTime24(schedule.endTime)}`;
  return `${days} · ${time}`;
}
export function isAvailableNow(schedule: Schedule, now: Date = new Date()): boolean {
  if (schedule.alwaysAvailable) return true;
  // Convert Sunday(0)..Saturday(6) -> Mon(0)..Sun(6)
  const jsDay = now.getDay();
  const dayIndex = jsDay === 0 ? 6 : jsDay - 1;
  if (!schedule.days.includes(dayIndex)) return false;
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = schedule.startTime.split(':').map(Number);
  const [eh, em] = schedule.endTime.split(':').map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  if (endMin <= startMin) {
    // overnight (e.g., 22:00 -> 02:00)
    return currentMin >= startMin || currentMin < endMin;
  }
  return currentMin >= startMin && currentMin < endMin;
}
export function ScheduleModal({
  open,
  initial,
  itemName,
  bulkCount,
  onClose,
  onSave
}: {
  open: boolean;
  initial: Schedule;
  itemName?: string;
  bulkCount?: number;
  onClose: () => void;
  onSave: (schedule: Schedule) => void;
}) {
  const [schedule, setSchedule] = useState<Schedule>(initial);
  useEffect(() => {
    if (open) setSchedule(initial);
  }, [open, initial]);
  const toggleDay = (dayIdx: number) => {
    setSchedule(prev => ({
      ...prev,
      days: prev.days.includes(dayIdx) ? prev.days.filter(d => d !== dayIdx) : [...prev.days, dayIdx].sort((a, b) => a - b)
    }));
  };
  const applyPreset = (preset: Schedule) => {
    setSchedule(preset);
  };
  const isPresetActive = (preset: Schedule) => {
    if (schedule.alwaysAvailable !== preset.alwaysAvailable) return false;
    if (preset.alwaysAvailable) return true;
    return schedule.startTime === preset.startTime && schedule.endTime === preset.endTime && schedule.days.length === preset.days.length && preset.days.every(d => schedule.days.includes(d));
  };
  const canSave = schedule.alwaysAvailable || schedule.days.length > 0;
  const previewAvailable = isAvailableNow(schedule);
  return <AnimatePresence data-id="element-3267">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" data-id="element-3268" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-3269">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto pointer-events-auto" data-id="element-3270">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between" data-id="element-3271">
                <div data-id="element-3272">
                  <div className="flex items-center gap-2 text-orange-500 mb-1" data-id="element-3273">
                    <Clock size={14} data-id="element-3274" />
                    <span className="text-xs font-semibold uppercase tracking-wide" data-id="element-3275">
                      Schedule Availability
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-3276">
                    {bulkCount ? `${bulkCount} items selected` : itemName}
                  </h2>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors -mr-2" data-id="element-3277">
                  <X size={18} data-id="element-3278" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-5" data-id="element-3279">
                {/* Always available toggle */}
                <button onClick={() => setSchedule(p => ({
              ...p,
              alwaysAvailable: !p.alwaysAvailable
            }))} className={`w-full p-4 rounded-xl border-2 transition-colors text-left flex items-center justify-between ${schedule.alwaysAvailable ? 'border-orange-500 bg-orange-50/50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3280">
                  <div data-id="element-3281">
                    <div className="font-semibold text-sm text-gray-900" data-id="element-3282">
                      Always available
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-3283">
                      Item is on the menu at all times
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${schedule.alwaysAvailable ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}`} data-id="element-3284">
                    {schedule.alwaysAvailable && <Check size={12} className="text-white" strokeWidth={3} data-id="element-3285" />}
                  </div>
                </button>

                {!schedule.alwaysAvailable && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} transition={{
              duration: 0.2
            }} className="space-y-5 overflow-hidden" data-id="element-3286">
                    {/* Presets */}
                    <div data-id="element-3287">
                      <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block" data-id="element-3288">
                        Quick presets
                      </label>
                      <div className="grid grid-cols-3 gap-2" data-id="element-3289">
                        {PRESETS.map(preset => {
                    const active = isPresetActive(preset.schedule);
                    return <button key={preset.label} onClick={() => applyPreset(preset.schedule)} className={`px-2 py-2.5 rounded-lg border text-xs font-medium transition-colors flex flex-col items-center gap-1 ${active ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3290">
                              {preset.icon}
                              {preset.label}
                            </button>;
                  })}
                      </div>
                    </div>

                    {/* Days */}
                    <div data-id="element-3291">
                      <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block" data-id="element-3292">
                        Days of week
                      </label>
                      <div className="flex gap-1.5" data-id="element-3293">
                        {DAY_LABELS.map((label, idx) => {
                    const active = schedule.days.includes(idx);
                    return <button key={idx} onClick={() => toggleDay(idx)} className={`flex-1 h-10 rounded-lg text-sm font-bold transition-colors ${active ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-id="element-3294">
                              {label}
                            </button>;
                  })}
                      </div>
                    </div>

                    {/* Time range */}
                    <div className="grid grid-cols-2 gap-3" data-id="element-3295">
                      <div data-id="element-3296">
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block" data-id="element-3297">
                          Start time
                        </label>
                        <input type="time" lang="en-GB" step={60} value={schedule.startTime} onChange={e => setSchedule(p => ({
                    ...p,
                    startTime: e.target.value
                  }))} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition [&::-webkit-datetime-edit-ampm-field]:hidden" data-id="element-3298" />
                      </div>
                      <div data-id="element-3299">
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block" data-id="element-3300">
                          End time
                        </label>
                        <input type="time" lang="en-GB" step={60} value={schedule.endTime} onChange={e => setSchedule(p => ({
                    ...p,
                    endTime: e.target.value
                  }))} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition [&::-webkit-datetime-edit-ampm-field]:hidden" data-id="element-3301" />
                      </div>
                    </div>
                  </motion.div>}

                {/* Preview */}
                <div className={`rounded-xl border p-4 ${previewAvailable ? 'bg-emerald-50/60 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-3302">
                  <div className="flex items-start gap-3" data-id="element-3303">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${previewAvailable ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-3304">
                      {previewAvailable && <span className="block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-75" data-id="element-3305" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-3306">
                      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${previewAvailable ? 'text-emerald-700' : 'text-gray-500'}`} data-id="element-3307">
                        {previewAvailable ? 'Available now' : 'Not available now'}
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-3308">
                        {formatSchedule(schedule)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white" data-id="element-3309">
                <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-3310">
                  Cancel
                </button>
                <button onClick={() => canSave && onSave(schedule)} disabled={!canSave} className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-3311">
                  Save schedule
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}